import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Truck, Pill, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline "Quick Fix" for costing errors — drops a missing product or shipping
 * rate straight into the price list without the xlsx round-trip.
 *
 * Props:
 *   mode: "product" | "shipping"
 *   defaultValues: {} pre-fill (product_name / shipment / country / weight_range)
 *   token: auth token
 *   onFixed: callback after successful insert + recheck
 */
const PRODUCT_FIELDS = [
  { key: "product_name", label: "Product Name", required: true, span: 2 },
  { key: "generic_name", label: "GENERIC NAME" },
  { key: "company", label: "Company" },
  { key: "box_packaging", label: "BOX PACKAGING", placeholder: "10x10" },
  { key: "weight_per_strip", label: "WEIGHT PER STRIP", placeholder: "60g" },
  { key: "unit", label: "UNIT", placeholder: "10" },
  { key: "category", label: "CATEGORY", placeholder: "ANTIBIOTICS" },
  { key: "price", label: "PRICE PER STRIP/ SACHETS/BOTTLES/PCS", required: true, placeholder: "45.00" },
  { key: "branded_mrp", label: "BRANDED (MRP)", placeholder: "180" },
  { key: "suggested_usd_price", label: "Suggested $ Price", placeholder: "2.85" },
  { key: "second_name", label: "2ND NAME" },
  { key: "image_link", label: "IMAGE LINK", span: 2 },
  { key: "narration", label: "NARRATION", span: 2 },
];

const SHIPPING_FIELDS = [
  { key: "shipment", label: "Shipment", required: true, placeholder: "ITPS / RMS / EMS" },
  { key: "country", label: "Country", required: true, placeholder: "USA" },
  { key: "weight_range", label: "Weight Range", required: true, placeholder: "0-250G" },
  { key: "cost", label: "Cost (INR)", required: true, placeholder: "720" },
  { key: "suggested_usd_price", label: "Suggested $ Price", placeholder: "8.60" },
];

const QuickFixButton = ({ mode = "product", defaultValues = {}, token, onFixed }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [impact, setImpact] = useState(null);  // { count: N } | null while loading
  // Cache the distinct services currently in shipping_prices so the
  // Shipment field can be a "combobox" — user can pick an existing service
  // from the dropdown OR type a brand-new code. Surfaces in the screenshot
  // case (`BELG` typed) right next to known options (`ITPS / RMS / EMS / SP`).
  const [knownShipments, setKnownShipments] = useState([]);

  const fields = mode === "product" ? PRODUCT_FIELDS : SHIPPING_FIELDS;

  // Lookup how many other error rows this fix will resolve (debounced).
  useEffect(() => {
    if (!open) { setImpact(null); return; }
    const params = new URLSearchParams({ mode });
    if (mode === "product") {
      const name = (form.product_name || "").trim();
      if (!name) { setImpact({ count: 0 }); return; }
      params.append("product_name", name);
    } else {
      const svc = (form.shipment || "").trim();
      const ctry = (form.country || "").trim();
      const wr = (form.weight_range || "").trim();
      if (!svc || !ctry) { setImpact({ count: 0 }); return; }
      params.append("shipment", svc);
      params.append("country", ctry);
      if (wr) params.append("weight_range", wr);
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/errors/costing/impact-count?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { if (!cancelled) setImpact({ count: 0 }); return; }
        const d = await res.json();
        if (!cancelled) setImpact({ count: d.count || 0 });
      } catch (_e) {
        if (!cancelled) setImpact({ count: 0 });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [open, mode, form.product_name, form.shipment, form.country, form.weight_range, token]);

  const reset = () => {
    setForm({ ...defaultValues });
  };

  const handleOpen = (next) => {
    setOpen(next);
    if (next) reset();
    // Hydrate distinct shipping services once when the dialog first opens
    // (mode === "shipping"). Cheap call → reused for every subsequent open.
    if (next && mode === "shipping" && knownShipments.length === 0) {
      (async () => {
        try {
          // Reuse the existing `/errors/shipping-services` endpoint that
          // already aggregates distinct shipment values from shipping_prices.
          // Avoids adding yet another endpoint just to feed a dropdown.
          const r = await fetch(`${BACKEND_URL}/api/errors/shipping-services`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) {
            const d = await r.json();
            const names = (d.services || [])
              .map((s) => s.service)
              .filter(Boolean)
              .sort();
            setKnownShipments(names);
          }
        } catch (_e) { /* fall back to free-text */ }
      })();
    }
  };

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    // Required-field validation
    for (const f of fields) {
      if (f.required && !(form[f.key] && String(form[f.key]).trim())) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      const endpoint = mode === "product" ? "/api/products" : "/api/shipping";
      const body = { ...form };
      // Backend expects strings for both product + shipping fields
      Object.keys(body).forEach((k) => {
        if (body[k] === undefined || body[k] === null) body[k] = "";
        if (typeof body[k] !== "string") body[k] = String(body[k]);
      });
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Failed to save ${mode}`);
        return;
      }

      // Trigger recheck so the error row resolves itself
      const recheckEndpoint = mode === "product"
        ? "/api/errors/recheck/products"
        : "/api/errors/recheck/shipping";
      try {
        await fetch(`${BACKEND_URL}${recheckEndpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
      } catch (_e) {
        // Recheck failures are non-fatal — the row will resolve on next refresh
      }

      toast.success(
        mode === "product"
          ? `Added "${form.product_name}" — costing recheck triggered.`
          : `Added shipping rate ${form.shipment} / ${form.country} / ${form.weight_range} — recheck triggered.`
      );
      setOpen(false);
      if (onFixed) onFixed();
    } catch (e) {
      console.error(e);
      toast.error(`Failed to save ${mode}`);
    } finally {
      setSaving(false);
    }
  };

  const accent = mode === "product"
    ? "text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
    : "text-orange-600 hover:text-orange-700 hover:bg-orange-50";

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        className={`h-5 w-5 p-0 ml-1 ${accent}`}
        onClick={(e) => { e.stopPropagation(); handleOpen(true); }}
        title={mode === "product" ? "Quick Add Product" : "Quick Add Shipping Rate"}
        data-testid={`quickfix-${mode}-btn`}
      >
        <Plus className="w-3.5 h-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "product" ? (
                <><Pill className="w-5 h-5 text-indigo-600" /> Quick Add Product to Price List</>
              ) : (
                <><Truck className="w-5 h-5 text-orange-600" /> Quick Add Shipping Rate</>
              )}
            </DialogTitle>
            <DialogDescription>
              {mode === "product"
                ? "Fill the price list fields below. Saves directly to Product Prices and re-runs the costing engine — no xlsx round-trip."
                : "Fill the shipping rate fields below. Saves directly to Shipping Rates and re-runs the costing engine — no xlsx round-trip."}
            </DialogDescription>
            {/* Show the parcel tracking number this fix originated from so
                the user can mentally tie the dialog to the row they clicked.
                Only renders when the parent supplied `tracking` in defaults. */}
            {defaultValues?.tracking && (
              <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-mono text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300" data-testid={`qf-${mode}-tracking-badge`}>
                <span className="text-slate-500 dark:text-slate-400 font-sans">For tracking:</span>
                <span className="font-semibold">{defaultValues.tracking}</span>
                {defaultValues?.client_code && (
                  <span className="font-sans text-slate-400">·</span>
                )}
                {defaultValues?.client_code && (
                  <span className="font-semibold">{defaultValues.client_code}</span>
                )}
              </div>
            )}
          </DialogHeader>

          {/* Impact counter — visible long-tail badge */}
          {impact !== null && impact.count > 0 && (
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                mode === "product"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300"
              }`}
              data-testid={`quickfix-impact-${mode}`}
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>
                Saving this will resolve <strong>{impact.count}</strong>{" "}
                costing error{impact.count === 1 ? "" : "s"} with the same{" "}
                {mode === "product" ? "product" : "service / country / weight slab"}.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {/* Hidden datalist sourced from existing shipping_prices.shipment
                values. Bound to the `shipment` Input below via list="…",
                giving us a combobox: pick from existing services OR type a
                brand-new one. Solves the user's "should be dropdown selection
                or new can write" request. */}
            {mode === "shipping" && knownShipments.length > 0 && (
              <datalist id="qf-shipment-options" data-testid="qf-shipment-datalist">
                {knownShipments.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
            {fields.map((f) => (
              <div
                key={f.key}
                className={f.span === 2 ? "sm:col-span-2" : ""}
              >
                <Label htmlFor={`qf-${f.key}`} className="text-xs">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                  {mode === "shipping" && f.key === "shipment" && knownShipments.length > 0 && (
                    <span className="ml-1 text-[10px] text-slate-500 font-normal">
                      ({knownShipments.length} known — pick or type new)
                    </span>
                  )}
                </Label>
                <Input
                  id={`qf-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  placeholder={f.placeholder || ""}
                  className="mt-1 text-sm"
                  list={mode === "shipping" && f.key === "shipment" && knownShipments.length > 0 ? "qf-shipment-options" : undefined}
                  data-testid={`qf-${mode}-${f.key}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className={mode === "product" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-orange-600 hover:bg-orange-700"}
              data-testid={`qf-${mode}-save-btn`}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Save & Recheck</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickFixButton;
